/**
 * Thin client for the Engine Orchestrator monitoring surface.
 *
 * The UI never runs psychometric/feature/intelligence/recommendation
 * or reporting math. It only:
 *   - asks the orchestrator to start a pipeline for a completed session
 *   - polls status until the `reports` stage is complete or a stage fails
 */
import { supabase } from "@/integrations/supabase/client";

export const ORCHESTRATOR_FN = "engine-orchestrator";

export type StageStatus = "pending" | "running" | "complete" | "failed" | "skipped";

export const PIPELINE_STAGES = [
  "assessment",
  "evidence",
  "features",
  "human_intelligence",
  "career_intelligence",
  "recommendations",
  "experience",
  "reports",
] as const;
export type PipelineStage = typeof PIPELINE_STAGES[number];

export interface PipelineStatus {
  learner_id: string;
  pipeline_id: string;
  assessment: StageStatus;
  evidence: StageStatus;
  features: StageStatus;
  human_intelligence: StageStatus;
  career_intelligence: StageStatus;
  recommendations: StageStatus;
  experience: StageStatus;
  reports: StageStatus;
  current_stage: PipelineStage;
  started_at: string | null;
  updated_at: string | null;
  duration_ms: number | null;
  failed_stage?: PipelineStage;
  error_message?: string;
}

/** By convention the pipeline_id equals the runtime session_id. */
export function pipelineIdForSession(session_id: string) { return session_id; }

/** Ask the orchestrator to begin the pipeline for a completed session. */
export async function startPipeline(session_id: string) {
  const { data, error } = await supabase.functions.invoke(ORCHESTRATOR_FN, {
    body: { action: "start", pipeline_id: pipelineIdForSession(session_id), session_id },
  });
  if (error) throw new Error(error.message);
  return data;
}

/** Resume the pipeline from its failed stage. */
export async function retryPipeline(session_id: string) {
  const { data, error } = await supabase.functions.invoke(ORCHESTRATOR_FN, {
    body: { action: "retry", pipeline_id: pipelineIdForSession(session_id) },
  });
  if (error) throw new Error(error.message);
  return data;
}

export async function getPipelineStatus(session_id: string): Promise<PipelineStatus | null> {
  const pipeline_id = pipelineIdForSession(session_id);
  const url = `${ORCHESTRATOR_FN}?pipeline_id=${encodeURIComponent(pipeline_id)}`;
  const { data, error } = await supabase.functions.invoke(url, { method: "GET" });
  if (error) throw new Error(error.message);
  if (data && typeof data === "object" && "error" in data) return null;
  return data as PipelineStatus;
}

/** Poll status every `intervalMs` until reports=complete, any stage fails, or aborted. */
export async function watchPipeline(
  session_id: string,
  onUpdate: (s: PipelineStatus) => void,
  opts: { intervalMs?: number; signal?: AbortSignal } = {},
): Promise<PipelineStatus> {
  const interval = opts.intervalMs ?? 2000;
  return new Promise((resolve, reject) => {
    const tick = async () => {
      if (opts.signal?.aborted) return reject(new Error("aborted"));
      try {
        const s = await getPipelineStatus(session_id);
        if (s) {
          onUpdate(s);
          if (s.reports === "complete") return resolve(s);
          if (PIPELINE_STAGES.some((st) => s[st] === "failed")) return resolve(s);
        }
      } catch (e) {
        // transient: keep polling unless aborted
        if (opts.signal?.aborted) return reject(e);
      }
      setTimeout(tick, interval);
    };
    tick();
  });
}
